import { useState, useEffect, useRef } from 'react';

interface UseSlideUpAnimationOptions {
  duration?: number; // Animation duration in milliseconds
  delay?: number; // Delay before animation starts
  staggerDelay?: number; // Delay between each digit animation
}

export const useSlideUpAnimation = (
  targetValue: number,
  options: UseSlideUpAnimationOptions = {},
) => {
  const { duration = 1500, delay = 300, staggerDelay = 50 } = options;

  const [currentValue, setCurrentValue] = useState(targetValue);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef<number | undefined>(undefined);
  const startTimeRef = useRef<number | undefined>(undefined);
  const previousTargetRef = useRef<number>(targetValue);

  useEffect(() => {
    // Only animate if target value has changed and is not 0 (to prevent animation on initial load/tab navigation)
    if (targetValue !== previousTargetRef.current && targetValue > 0) {
      previousTargetRef.current = targetValue;

      // Reset animation
      setCurrentValue(0);
      setIsAnimating(true);

      // Clear any existing animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      // Start animation after delay
      setTimeout(() => {
        startTimeRef.current = performance.now();

        const animate = (currentTime: number) => {
          if (!startTimeRef.current) return;

          const elapsed = currentTime - startTimeRef.current;
          const progress = Math.min(elapsed / duration, 1);

          // Easing function for smooth animation
          const easeOutCubic = 1 - Math.pow(1 - progress, 3);
          const animatedValue = Math.floor(targetValue * easeOutCubic);

          setCurrentValue(animatedValue);

          if (progress < 1) {
            animationRef.current = requestAnimationFrame(animate);
          } else {
            setCurrentValue(targetValue);
            setIsAnimating(false);
          }
        };

        animationRef.current = requestAnimationFrame(animate);
      }, delay);
    } else if (targetValue !== previousTargetRef.current) {
      // If target value changed but is 0 or null, just update without animation
      previousTargetRef.current = targetValue;
      setCurrentValue(targetValue);
      setIsAnimating(false);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetValue, duration, delay]);

  // Split number into individual digits for staggered animation
  const getAnimatedDigits = () => {
    const numberString = currentValue.toLocaleString();
    return numberString.split('').map((char, index) => ({
      char,
      index,
      style: {
        display: 'inline-block',
        transform: isAnimating ? 'translateY(0)' : 'translateY(0)',
        opacity: 1,
        transition: `all 0.3s ease-out`,
        transitionDelay: `${index * staggerDelay}ms`,
      },
    }));
  };

  return {
    value: currentValue,
    isAnimating,
    digits: getAnimatedDigits(),
    progress: targetValue > 0 ? currentValue / targetValue : 0,
  };
};

export default useSlideUpAnimation;
